// components/tracker/calendar-drug-strip.tsx
//
// THE DRUG WATCH STRIP — Paper LA2-0, node N2C-0 ("Drug watch strip"),
// ported 2026-08-26 (FE-READOUTS-1 Phase C). Sits between the hero and the
// Timeline | Calendar toggle so BOTH views see it, as on the board.
//
// Grammar follows digest-entity-rail (the strip precedent): the drug's
// sphere avatar from the identity context (asset-seeded, FE-DRUG-AVATAR-1)
// plus the per-drug trial count. Cells deep-link to the Trials page filtered
// by that drug, the same figure the count claims, which is what keeps the
// number checkable.
//
// HONESTY DEVIATION FROM THE BOARD'S COPY: the artboard's meta line says
// "229 ACTIVE TRIALS", but `surface.drugCounts` counts the drug's trials on
// this WATCH (all statuses the watch holds), not "active" ones — so the
// line reads "229 TRIALS ON WATCH". A drug with no count entry renders an
// empty meta line, never a zero: unknown is not nought (digest rail rule).

import Link from "next/link"

import { useDrugAvatar } from "@/components/tracker/drug-identity-context"
import { displayName } from "@/components/tracker/track-copy"

import styles from "@/components/tracker/readouts-page.module.css"

function countFor(
  counts: Record<string, number> | undefined,
  drug: string,
): number | undefined {
  if (!counts) return undefined
  const exact = counts[drug]
  if (typeof exact === "number") return exact
  const key = Object.keys(counts).find(
    (k) => k.toLowerCase() === drug.toLowerCase(),
  )
  return key ? counts[key] : undefined
}

function countLabel(n: number): string {
  if (n === 0) return "No trials on watch"
  return `${n.toLocaleString("en-GB")} ${n === 1 ? "trial" : "trials"} on watch`
}

function StripCell({
  drug,
  count,
}: {
  drug: string
  count: number | undefined
}) {
  const { src, identity } = useDrugAvatar(drug)
  const n = count
  return (
    <Link
      href={`/tools/trial-tracker/trials?drug=${encodeURIComponent(drug)}`}
      className={styles.stripCell}
      title={
        typeof n === "number"
          ? `${displayName(drug)} · ${countLabel(n).toLowerCase()}`
          : displayName(drug)
      }
    >
      <span className={styles.stripIdentity}>
        <span
          className={styles.stripAvatar}
          aria-hidden="true"
          data-identity={identity}
          style={{ backgroundImage: `url("${src}")` }}
        />
        <span className={styles.stripName}>{displayName(drug)}</span>
      </span>
      <span className={styles.stripCount}>
        {typeof n === "number" ? countLabel(n) : ""}
      </span>
    </Link>
  )
}

export function CalendarDrugStrip({
  drugs,
  counts,
}: {
  /** The MERGED roster, in roster order. */
  drugs: string[]
  /** Whole-watch trials per drug (`surface.drugCounts`). */
  counts?: Record<string, number>
}) {
  if (drugs.length === 0) return null

  return (
    <section className={styles.strip} aria-label="Your drug watch">
      <div className={styles.stripRow}>
        {drugs.map((drug) => (
          <StripCell key={drug} drug={drug} count={countFor(counts, drug)} />
        ))}
      </div>
      <Link href="/tools/trial-tracker" className={styles.stripManage}>
        <span aria-hidden>☷</span> Manage watch
      </Link>
    </section>
  )
}
